import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { IsLoading, useLoading } from "../../../common/components/AppState/IsLoading";
import { LoadingState } from "../../../common/state/slice/appStateSlice";
import { ButtonSquareIconsProject } from "../../../common/components/Button";
import type { IProjectGroupElementProject } from "../../../model/model";
import type { ThreeDotsItemKind } from "../../../common/components/Icon/ThreeDotsDropdownMenu";
import {
  PROJECT_LIST_COMPONENT_CLONE_FROM_ASSIGN_ID,
  PROJECT_LIST_COMPONENT_CLONE_LOADING_ID,
} from "src/pages/Projects/ProjectsList";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import { getSelectedProjectGroup } from "src/features/projectGroups/state/projectGroupsSlice";
import { PROJECT_GROUP_LIST_COMPONENT_ID } from "src/features/projectGroups/components/ProjectGroupList";
import { launchTour } from "src/features/onboarding/state/onboardingSlice";
import { useTourView } from "src/features/onboarding/engine/useTourView";
import { TOUR_VIEW_ANCHORS } from "src/features/onboarding/engine/tourViewAnchors";
import EmptyState from "src/features/statsNew/components/EmptyState";

export const PROJECT_LIST_COMPONENT_ID = "projectListComponent";

type ProjectListProps = {
  projects: IProjectGroupElementProject[];
  componentId?: string;
  handleClick: (project: IProjectGroupElementProject) => void;
  handleThreeDotButtonClick(
    this: void,
    e: { item: ThreeDotsItemKind; itemId: number; action: string },
  ): void;
  setEditedProjectId?: (id: number) => void;
};

const ProjectList = ({
  handleThreeDotButtonClick,
  handleClick,
  projects,
  setEditedProjectId,
}: ProjectListProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedProjectGroup = useAppSelector(getSelectedProjectGroup);
  const modulesLoadingState = useLoading(PROJECT_GROUP_LIST_COMPONENT_ID);
  const hasModulesLoadedOnce = modulesLoadingState === LoadingState.Success;
  const shownProjects = projects.filter((project: IProjectGroupElementProject) =>
    project ? !project.is_template : false,
  );
  const latestProjectId = shownProjects.reduce(
    (latest: number, project: IProjectGroupElementProject) =>
      Math.max(latest, Number(project.id) || 0),
    0,
  );
  const listLoadingState = useLoading(PROJECT_LIST_COMPONENT_ID);
  const cloneLoadingState = useLoading(PROJECT_LIST_COMPONENT_CLONE_LOADING_ID);
  useTourView(
    "course-modules-cards",
    [modulesLoadingState, listLoadingState, cloneLoadingState].every(
      (state) => state !== LoadingState.Loading,
    ),
    shownProjects.length > 0 ? TOUR_VIEW_ANCHORS["course-modules-cards"] : [],
  );

  return (
    <IsLoading componentId={PROJECT_LIST_COMPONENT_ID} showSuccess={false}>
      <IsLoading
        componentId={PROJECT_LIST_COMPONENT_CLONE_LOADING_ID}
        showSuccess={false}
        spinnerPlaceholder={true}
        spinnerStyle={{
          top: "50%",
          left: "50%",
          position: "fixed",
        }}
      >
        <div className="fixed z-50">
          <IsLoading
            componentId={PROJECT_LIST_COMPONENT_CLONE_FROM_ASSIGN_ID}
            showSuccess={false}
            spinnerPlaceholder
            spinnerStyle={{
              top: "50%",
              left: "50%",
              position: "fixed",
            }}
          />
        </div>
        {projects.length > 0 ? (
          <div
            className={`w-full max-w-9xl grid grid-cols-2
        md:grid-cols-4 lg:grid-cols-6 gap-6 md:gap-10 mx-auto`}
          >
            {shownProjects.map((project: IProjectGroupElementProject, i: number) => {
              return (
                <IsLoading
                  key={`${i + 9}//;;${project.id}`}
                  componentId={`project-${project.id}`}
                  showSuccess={false}
                  spinnerPlaceholder
                >
                  <div
                    data-tour={
                      Number(project.id) === latestProjectId ? "module-card-latest" : undefined
                    }
                  >
                    <div data-tour="module-card-open">
                      <ButtonSquareIconsProject
                        setEditedProjectId={setEditedProjectId}
                        handleThreeDotButtonClick={(e: {
                          item: ThreeDotsItemKind;
                          itemId: number;
                          action: string;
                        }) => {
                          handleThreeDotButtonClick(e);
                        }}
                        currentProject={project.id}
                        project={project}
                        onClick={() => handleClick(project)}
                      />
                    </div>
                  </div>
                </IsLoading>
              );
            })}
          </div>
        ) : (
          <div className="absolute h-72 left-[40%] pt-3">
            {hasModulesLoadedOnce && (
              <EmptyState
                icon="folder"
                title={t("onboarding.emptyStates.modules.title")}
                description={t("onboarding.emptyStates.modules.description")}
                action={{
                  label: t("onboarding.emptyStates.modules.cta"),
                  onClick: () => navigate(`/courses/${selectedProjectGroup.id}/new`),
                }}
              >
                <button
                  type="button"
                  className="btn-alternative-outline mt-2"
                  onClick={() =>
                    dispatch(launchTour({ id: "course-modules", source: "empty_state" }))
                  }
                >
                  {t("onboarding.emptyStates.tourCta")}
                </button>
              </EmptyState>
            )}
          </div>
        )}
      </IsLoading>
    </IsLoading>
  );
};

export default ProjectList;
